import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { Response } from '../../../../../_shared/models';
import { Pagination } from '../../../../../_shared/models/pagination-response.model';
import { DetectedAccidentStatus, DetectedAccidentStatusLabelKeys } from '../../enums/detected-accident-status.enum';
import { DetectedAccident } from '../../models/accidents-and-incidents-response.model';
import { AccidentsAndIncidentsRequest } from '../../models/accidents-and-incidents-request.model';
import { AccidentsAndIncidentsService } from '../../services/accidents-and-incidents.service';
import {
  FALSE_ALARM_NEXT_STEP,
  FORM_MODAL_OPTIONS,
  INCIDENT_DETAILS_MODAL_OPTIONS
} from '../../constants/accidents-and-incidents.constants';
import { ClaimInsuranceModalComponent } from '../modals/claim-insurance-modal/claim-insurance-modal.component';
import { IncidentDetailsModalComponent } from '../modals/incident-details-modal/incident-details-modal.component';
import { AttachmentsPillComponent } from '../attachments-pill/attachments-pill.component';
import { DriverCallStatusComponent } from '../driver-call-status/driver-call-status.component';
import { AccidentsTableBase } from '../accidents-table-base';

@Component({
  selector: 'app-detected-accident',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    PaginationComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    AttachmentsPillComponent,
    DriverCallStatusComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './detected-accident.component.html',
  styleUrl: './detected-accident.component.scss'
})
export class DetectedAccidentComponent extends AccidentsTableBase<DetectedAccident> {
  private accidentsAndIncidentsService: AccidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private modalService: NgbModal = inject(NgbModal);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  readonly detectedAccidentStatus = DetectedAccidentStatus;
  readonly statusLabelKeys = DetectedAccidentStatusLabelKeys;

  onExport(): void {
    // TODO: Implement export functionality
  }

  onClaimInsurance(accident: DetectedAccident): void {
    const modalRef = this.modalService.open(ClaimInsuranceModalComponent, {
      ...FORM_MODAL_OPTIONS,
      windowClass: 'claim-insurance-modal-window'
    });
    modalRef.componentInstance.title = accident.accidentNumber;
    modalRef.componentInstance.accident = accident;
    modalRef.componentInstance.assetId = accident.assetId;
    modalRef.componentInstance.accidentId = accident.accidentId;
    modalRef.result.then(
      () => this.reloadAfterRowRemoved(),
      () => {}
    );
  }

  onViewLocation(accident: DetectedAccident): void {
    const modalRef = this.modalService.open(IncidentDetailsModalComponent, INCIDENT_DETAILS_MODAL_OPTIONS);
    modalRef.componentInstance.title = accident.accidentNumber;
    modalRef.componentInstance.latitude = accident.latitude;
    modalRef.componentInstance.longitude = accident.longitude;
    modalRef.componentInstance.locationText = accident.location;
  }

  getStatus(accident: DetectedAccident): DetectedAccidentStatus {
    if (!accident.nextStep) {
      return DetectedAccidentStatus.NotVerified;
    }

    return accident.nextStep === FALSE_ALARM_NEXT_STEP
      ? DetectedAccidentStatus.FalsePositive
      : DetectedAccidentStatus.Verified;
  }

  getStatusClass(status: DetectedAccidentStatus): string {
    switch (status) {
      case DetectedAccidentStatus.Verified:
        return 'status-verified';
      case DetectedAccidentStatus.FalsePositive:
        return 'status-false-positive';
      case DetectedAccidentStatus.NotVerified:
        return 'status-not-verified';
    }
  }

  isClaimDisabled(accident: DetectedAccident): boolean {
    return this.getStatus(accident) === DetectedAccidentStatus.FalsePositive;
  }

  protected fetchPage(request: AccidentsAndIncidentsRequest, cancellation: Subject<void>): Promise<Response<Pagination<DetectedAccident[]>>> {
    return this.accidentsAndIncidentsService.getDetectedAccidents(request, cancellation);
  }
}